import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import {
  LogOut, GraduationCap, Users, Building2, FolderKanban, Wallet, FlaskConical, Clock,
  CheckCircle, XCircle, Copy, Upload, Download, Plus, Loader2, IndianRupee, Trash2,
  Settings as SettingsIcon, X, Pencil, UserCheck, Megaphone, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import FinanceSummary from '@/components/FinanceSummary';
import { UniversityOverviewDialog, ModuleDisabledNotice } from '@/components/UniversityOverviewDialogs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtINR = (paise) => `₹${((paise || 0) / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export default function UniversityDashboard() {
  const navigate = useNavigate();
  const [token] = useState(() => localStorage.getItem('collabhive_uni_token'));
  const [uni, setUni] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [dialog, setDialog] = useState(null);

  const loadStats = useCallback(async () => {
    const res = await fetch(`${API}/university/dashboard`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setStats(await res.json());
  }, [token]);
  useEffect(() => {
    if (!token) { navigate('/university/login'); return; }
    (async () => {
      try {
        const [meRes, dashRes] = await Promise.all([
          fetch(`${API}/university/me`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API}/university/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (meRes.status === 401 || meRes.status === 403) {
          localStorage.removeItem('collabhive_uni_token');
          navigate('/university/login');
          return;
        }
        if (meRes.ok) setUni((await meRes.json()).university);
        if (dashRes.ok) setStats(await dashRes.json());
      } finally { setLoading(false); }
    })();
  }, [token, navigate]);

  const logout = () => {
    localStorage.removeItem('collabhive_uni_token');
    localStorage.removeItem('collabhive_uni_name');
    navigate('/university/login');
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (!uni) return null;

  const refExpert = `${window.location.origin}/expert/signup?ref=${stats?.referral_links?.experts_token}`;
  const refIndustry = `${window.location.origin}/industry/auth?ref=${stats?.referral_links?.industry_token}`;

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`));
  };

  const counts = stats?.counts || {};
  const modules = stats?.modules || uni.modules || { projects: true, opportunities: true, labs: true };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-white sticky top-0 z-20">
        <div className="container-main py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <GraduationCap className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold truncate text-sm sm:text-base">{uni.name}</p>
              <p className="text-[10px] text-muted-foreground truncate">University Admin · {uni.admin_email}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={() => setSettingsOpen(true)} className="gap-1" data-testid="uni-settings-btn">
              <SettingsIcon className="w-4 h-4" /> <span className="hidden sm:inline">Settings</span>
            </Button>
            <Button size="sm" variant="ghost" onClick={logout} className="gap-1" data-testid="uni-logout-btn">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>University Settings</DialogTitle>
            <DialogDescription>Onboarding form customisation and revenue distribution</DialogDescription>
          </DialogHeader>
          <SettingsTab token={token} />
        </DialogContent>
      </Dialog>

      <main className="container-main py-6">
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="w-full grid grid-cols-3 sm:grid-cols-6 h-auto mb-5">
            <TabsTrigger value="overview" className="text-xs sm:text-sm py-2 flex-col sm:flex-row gap-1 sm:gap-2" data-testid="tab-overview">
              <FolderKanban className="w-4 h-4" /> <span>Overview</span>
            </TabsTrigger>
            <TabsTrigger value="experts" className="text-xs sm:text-sm py-2 flex-col sm:flex-row gap-1 sm:gap-2" data-testid="tab-experts">
              <Users className="w-4 h-4" /> <span>Experts</span>
            </TabsTrigger>
            <TabsTrigger value="industry" className="text-xs sm:text-sm py-2 flex-col sm:flex-row gap-1 sm:gap-2" data-testid="tab-industry">
              <Building2 className="w-4 h-4" /> <span>Industry</span>
            </TabsTrigger>
            <TabsTrigger value="labs" className="text-xs sm:text-sm py-2 flex-col sm:flex-row gap-1 sm:gap-2" data-testid="tab-labs">
              <FlaskConical className="w-4 h-4" /> <span>Labs</span>
            </TabsTrigger>
            <TabsTrigger value="bookings" className="text-xs sm:text-sm py-2 flex-col sm:flex-row gap-1 sm:gap-2" data-testid="tab-bookings">
              <Clock className="w-4 h-4" /> <span>Bookings</span>
            </TabsTrigger>
            <TabsTrigger value="finance" className="text-xs sm:text-sm py-2 flex-col sm:flex-row gap-1 sm:gap-2" data-testid="tab-finance">
              <Wallet className="w-4 h-4" /> <span>Finance</span>
            </TabsTrigger>
          </TabsList>

          {/* ---------- OVERVIEW ---------- */}
          <TabsContent value="overview" className="mt-0 space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <StatCard icon={Users} label="Innovators Onboarded" value={counts.experts} />
              <StatCard icon={Building2} label="Industry Onboarded" value={counts.industry} />
              <StatCard icon={FolderKanban} label="Projects Involved" value={counts.projects} onClick={() => setDialog('involved')} />
              <StatCard icon={CheckCircle} label="Innovators Selected" value={counts.selected} />
              <StatCard icon={FolderKanban} label="Projects Notified" value={counts.notified} onClick={() => setDialog('notified')} />
              <StatCard icon={CheckCircle} label="Projects Completed" value={counts.completed} />
              <StatCard icon={Upload} label="Submissions Received" value={counts.submissions} onClick={() => setDialog('submissions')} />
              <StatCard icon={FlaskConical} label="Labs Listed" value={counts.labs} />
              <StatCard icon={UserCheck} label="Innovators Selection" value={counts.innovator_selection} onClick={() => setDialog('selection')} disabled={!modules.projects} />
              <StatCard icon={Megaphone} label="Opportunities" value={counts.opportunities} onClick={() => setDialog('opportunities')} disabled={!modules.opportunities} />
            </div>
            <UniversityOverviewDialog kind={dialog} onClose={() => setDialog(null)} token={token} modules={modules} onChanged={loadStats} />

            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Copy className="w-4 h-4 text-primary" /> Referral Links</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-xs">Expert Referral Link</Label>
                  <div className="flex gap-2 mt-1">
                    <Input readOnly value={refExpert} className="font-mono text-xs" data-testid="ref-expert-link" />
                    <Button variant="outline" size="sm" onClick={() => copy(refExpert, 'Expert link')} data-testid="copy-expert-link"><Copy className="w-4 h-4" /></Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">Share with your faculty / researchers. They'll see a special signup form capturing designation, department, school and employee/student ID.</p>
                </div>
                <div>
                  <Label className="text-xs">Industry Referral Link</Label>
                  <div className="flex gap-2 mt-1">
                    <Input readOnly value={refIndustry} className="font-mono text-xs" data-testid="ref-industry-link" />
                    <Button variant="outline" size="sm" onClick={() => copy(refIndustry, 'Industry link')} data-testid="copy-industry-link"><Copy className="w-4 h-4" /></Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">Share with industry partners you want to onboard; their activity will appear on your dashboard.</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---------- EXPERTS ---------- */}
          <TabsContent value="experts" className="mt-0">
            <ExpertsTab token={token} />
          </TabsContent>

          {/* ---------- INDUSTRY ---------- */}
          <TabsContent value="industry" className="mt-0">
            <IndustryTab token={token} />
          </TabsContent>

          {/* ---------- LABS ---------- */}
          <TabsContent value="labs" className="mt-0">
            <LabsTab token={token} disabled={!modules.labs} />
          </TabsContent>

          {/* ---------- BOOKINGS ---------- */}
          <TabsContent value="bookings" className="mt-0">
            <BookingsTab token={token} disabled={!modules.labs} />
          </TabsContent>

          {/* ---------- FINANCE ---------- */}
          <TabsContent value="finance" className="mt-0">
            <UniversityFinance token={token} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, onClick, disabled }) {
  const testId = `stat-${label.replace(/\s+/g,'-').toLowerCase()}`;
  return (
    <Card
      data-testid={testId}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      className={onClick ? 'cursor-pointer group hover:border-primary/60 hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow,border-color] duration-200' : ''}
    >
      <CardContent className="p-4">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</p>
        <div className="flex items-end justify-between mt-1">
          <p className="text-xl sm:text-2xl font-bold">{value ?? 0}</p>
          {disabled ? <Badge className="bg-amber-500/10 text-amber-700 text-[9px]" data-testid={`${testId}-disabled`}>Disabled</Badge>
            : onClick && <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-transform" />}
        </div>
      </CardContent>
    </Card>
  );
}

function ExpertsTab({ token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      const res = await fetch(`${API}/university/experts`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setRows((await res.json()).experts || []);
      setLoading(false);
    })();
  }, [token]);
  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto my-6" />;
  if (!rows.length) return <p className="text-sm text-muted-foreground py-6 text-center">No innovators have signed up via your referral yet. Share your expert link to onboard them.</p>;
  return (
    <div className="space-y-3">
      {rows.map(e => (
        <Card key={e.id} data-testid={`uni-expert-${e.id}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
              <div>
                <h4 className="font-medium">{e.name}</h4>
                <p className="text-xs text-muted-foreground">{e.email}</p>
              </div>
              <div className="flex gap-2">
                <Badge variant="secondary" className="text-[10px]">{e.projects_count || 0} projects</Badge>
                <Badge variant="secondary" className="text-[10px]">{e.submissions_count || 0} submissions</Badge>
                {e.is_approved ? <Badge className="bg-emerald-500/10 text-emerald-700 text-[10px]">Approved</Badge> : <Badge className="bg-amber-500/10 text-amber-700 text-[10px]">Pending</Badge>}
              </div>
            </div>
            {(e.designation || e.department || e.school) && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mt-2 pt-2 border-t">
                <div><p className="text-[10px] text-muted-foreground">Designation</p><p className="font-medium">{e.designation || '—'}</p></div>
                <div><p className="text-[10px] text-muted-foreground">Department</p><p className="font-medium">{e.department || '—'}</p></div>
                <div><p className="text-[10px] text-muted-foreground">School / Faculty</p><p className="font-medium">{e.school || '—'}</p></div>
                <div><p className="text-[10px] text-muted-foreground">Employee / Student ID</p><p className="font-medium">{e.employee_id || '—'}</p></div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function IndustryTab({ token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      const res = await fetch(`${API}/university/industry`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setRows((await res.json()).industry || []);
      setLoading(false);
    })();
  }, [token]);
  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto my-6" />;
  if (!rows.length) return <p className="text-sm text-muted-foreground py-6 text-center">No industry users yet. Share your industry referral link.</p>;
  return (
    <div className="space-y-3">
      {rows.map(u => (
        <Card key={u.id} data-testid={`uni-industry-${u.id}`}>
          <CardContent className="p-4 flex items-center justify-between gap-3">
            <div>
              <h4 className="font-medium">{u.company_name || '(no company)'}</h4>
              <p className="text-xs text-muted-foreground">{u.email}</p>
            </div>
            <Badge variant="secondary" className="text-[10px]">{u.projects_count || 0} projects</Badge>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function LabsTab({ token, disabled }) {
  const emptyForm = { name:'', area_of_expertise:'', equipment:[''], capacity:'', rate_per_hour_inr:0, rate_per_day_inr:0, contact_person:'', contact_email:'' };
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [importing, setImporting] = useState(false);
  const [editLab, setEditLab] = useState(null);
  const [editForm, setEditForm] = useState({ rate_per_hour_inr:0, rate_per_day_inr:0, contact_person:'', contact_email:'' });
  const [savingEdit, setSavingEdit] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`${API}/university/labs`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setLabs((await res.json()).labs || []);
    setLoading(false);
  }, [token]);
  useEffect(() => { refresh(); }, [refresh]);

  const downloadTemplate = async () => {
    const res = await fetch(`${API}/university/labs/template`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error('Download failed'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'collabhive-labs-template.xlsx'; a.click();
    URL.revokeObjectURL(url);
  };

  const importExcel = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${API}/university/labs/import`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await res.json();
      if (res.ok) { toast.success(`Imported ${data.imported} labs (${data.skipped} skipped)`); refresh(); }
      else toast.error(data.detail || 'Import failed');
    } finally { setImporting(false); e.target.value = ''; }
  };

  const saveLab = async (e) => {
    e.preventDefault();
    const equipment = (form.equipment || []).map(x => (x || '').trim()).filter(Boolean);
    if (!equipment.length) { toast.error('Add at least one equipment item'); return; }
    if (!(form.contact_email || '').trim()) { toast.error('Contact email is required'); return; }
    if (!Number(form.rate_per_hour_inr) && !Number(form.rate_per_day_inr)) { toast.error('Enter at least one rate (per hour or per day)'); return; }
    const res = await fetch(`${API}/university/labs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ...form, equipment, rate_per_hour_inr: Number(form.rate_per_hour_inr)||0, rate_per_day_inr: Number(form.rate_per_day_inr)||0 }),
    });
    if (res.ok) { toast.success('Lab added'); setAddOpen(false); setForm(emptyForm); refresh(); }
    else { const d = await res.json().catch(() => ({})); toast.error(d.detail || 'Failed'); }
  };

  const openEdit = (lab) => {
    setEditLab(lab);
    setEditForm({
      rate_per_hour_inr: lab.rate_per_hour_inr || 0,
      rate_per_day_inr: lab.rate_per_day_inr || 0,
      contact_person: lab.contact_person || '',
      contact_email: lab.contact_email || '',
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!(editForm.contact_email || '').trim()) { toast.error('Contact email is required'); return; }
    if (!Number(editForm.rate_per_hour_inr) && !Number(editForm.rate_per_day_inr)) { toast.error('Enter at least one rate (per hour or per day)'); return; }
    setSavingEdit(true);
    try {
      const res = await fetch(`${API}/university/labs/${editLab.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          rate_per_hour_inr: Number(editForm.rate_per_hour_inr)||0,
          rate_per_day_inr: Number(editForm.rate_per_day_inr)||0,
          contact_person: editForm.contact_person,
          contact_email: editForm.contact_email,
        }),
      });
      if (res.ok) { toast.success('Lab updated'); setEditLab(null); refresh(); }
      else { const d = await res.json().catch(() => ({})); toast.error(d.detail || 'Failed'); }
    } finally { setSavingEdit(false); }
  };

  const deleteLab = async (id) => {
    if (!window.confirm('Remove this lab?')) return;
    const res = await fetch(`${API}/university/labs/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { toast.success('Removed'); refresh(); }
  };

  return (
    <div className="space-y-4">
      {disabled && <ModuleDisabledNotice module="Labs" testId="module-disabled-labs" />}
      <div className="flex gap-2 flex-wrap">
        <Button variant="outline" size="sm" onClick={downloadTemplate} className="gap-2" data-testid="download-labs-template"><Download className="w-4 h-4" /> Download Excel Template</Button>
        <label className={disabled ? 'pointer-events-none opacity-50' : ''}>
          <input type="file" accept=".xlsx,.xls" className="hidden" onChange={importExcel} disabled={disabled} data-testid="import-labs-file" />
          <Button variant="outline" size="sm" asChild disabled={importing || disabled}>
            <span className="gap-2 cursor-pointer inline-flex items-center">
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Import from Excel
            </span>
          </Button>
        </label>
        <Button size="sm" onClick={() => setAddOpen(true)} disabled={disabled} className="gap-2" data-testid="add-lab-btn"><Plus className="w-4 h-4" /> Add Lab</Button>
      </div>

      {loading ? <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto my-6" /> :
        !labs.length ? <p className="text-sm text-muted-foreground py-6 text-center">No labs yet. Import via Excel or add one.</p> :
        <div className="space-y-3">
          {labs.map(l => (
            <Card key={l.id} data-testid={`lab-${l.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <h4 className="font-medium flex items-center gap-2"><FlaskConical className="w-4 h-4 text-primary" /> {l.name}</h4>
                    <p className="text-sm text-muted-foreground mt-1">{l.area_of_expertise}</p>
                    {(l.equipment && l.equipment.length > 0) ? (
                      <div className="mt-2">
                        <p className="text-xs text-muted-foreground mb-1"><b>Equipment:</b></p>
                        <div className="flex flex-wrap gap-1">
                          {l.equipment.map((eq, idx) => (
                            <Badge key={idx} variant="outline" className="text-[10px]">{eq}</Badge>
                          ))}
                        </div>
                      </div>
                    ) : l.equipment_available ? (
                      <p className="text-xs text-muted-foreground mt-2"><b>Equipment:</b> {l.equipment_available}</p>
                    ) : null}
                    <div className="flex gap-3 text-xs mt-2 flex-wrap">
                      {l.rate_per_hour_inr>0 && <Badge variant="secondary">₹{l.rate_per_hour_inr}/hr</Badge>}
                      {l.rate_per_day_inr>0 && <Badge variant="secondary">₹{l.rate_per_day_inr}/day</Badge>}
                      {l.capacity && <Badge variant="outline">{l.capacity}</Badge>}
                      {l.contact_person && <Badge variant="outline">{l.contact_person}</Badge>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" disabled={disabled} onClick={() => openEdit(l)} data-testid={`edit-lab-${l.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" disabled={disabled} onClick={() => deleteLab(l.id)} className="text-destructive" data-testid={`delete-lab-${l.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      }

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Lab</DialogTitle><DialogDescription>Make this lab available to industry browsers</DialogDescription></DialogHeader>
          <form onSubmit={saveLab} className="space-y-3">
            <div><Label>Lab Name *</Label><Input required value={form.name} onChange={e=>setForm({...form, name:e.target.value})} /></div>
            <div><Label>Area of Expertise *</Label><Input required value={form.area_of_expertise} onChange={e=>setForm({...form, area_of_expertise:e.target.value})} /></div>
            <div>
              <Label>Equipment Items *</Label>
              <div className="space-y-2 mt-1">
                {(form.equipment || ['']).map((eq, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      required={idx === 0}
                      value={eq}
                      onChange={(e) => {
                        const next = [...(form.equipment || [''])];
                        next[idx] = e.target.value;
                        setForm({ ...form, equipment: next });
                      }}
                      placeholder={idx === 0 ? "e.g. Potentiostat (Bio-Logic VMP3)" : "Another equipment item"}
                      data-testid={`lab-equipment-${idx}`}
                    />
                    <Button type="button" size="sm" variant="ghost" onClick={() => {
                      const next = [...(form.equipment || [''])];
                      next.splice(idx, 1);
                      setForm({ ...form, equipment: next.length ? next : [''] });
                    }}><X className="w-4 h-4" /></Button>
                  </div>
                ))}
                <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, equipment: [...(form.equipment || ['']), ''] })} className="gap-1" data-testid="add-equipment-btn">
                  <Plus className="w-3 h-3" /> Add equipment
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Capacity</Label><Input value={form.capacity} onChange={e=>setForm({...form, capacity:e.target.value})} placeholder="4 projects / week" /></div>
              <div><Label>Contact Person</Label><Input value={form.contact_person} onChange={e=>setForm({...form, contact_person:e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Rate per Hour (INR) *</Label><Input type="number" min="0" value={form.rate_per_hour_inr} onChange={e=>setForm({...form, rate_per_hour_inr:e.target.value})} data-testid="lab-rate-hour" /></div>
              <div><Label>Rate per Day (INR) *</Label><Input type="number" min="0" value={form.rate_per_day_inr} onChange={e=>setForm({...form, rate_per_day_inr:e.target.value})} data-testid="lab-rate-day" /></div>
            </div>
            <p className="text-[10px] text-muted-foreground -mt-1">At least one rate must be greater than 0.</p>
            <div><Label>Contact Email *</Label><Input type="email" required value={form.contact_email} onChange={e=>setForm({...form, contact_email:e.target.value})} data-testid="lab-contact-email" /></div>
            <DialogFooter><Button type="submit" data-testid="save-lab-btn">Save Lab</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editLab} onOpenChange={(v) => !v && setEditLab(null)}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          {editLab && (
            <>
              <DialogHeader>
                <DialogTitle>Edit Lab — {editLab.name}</DialogTitle>
                <DialogDescription>Rates and contact details. Name, expertise and equipment stay unchanged.</DialogDescription>
              </DialogHeader>
              <form onSubmit={saveEdit} className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Rate per Hour (INR) *</Label><Input type="number" min="0" value={editForm.rate_per_hour_inr} onChange={e=>setEditForm({...editForm, rate_per_hour_inr:e.target.value})} data-testid="edit-lab-rate-hour" /></div>
                  <div><Label>Rate per Day (INR) *</Label><Input type="number" min="0" value={editForm.rate_per_day_inr} onChange={e=>setEditForm({...editForm, rate_per_day_inr:e.target.value})} data-testid="edit-lab-rate-day" /></div>
                </div>
                <p className="text-[10px] text-muted-foreground -mt-1">At least one rate must be greater than 0.</p>
                <div><Label>Contact Person</Label><Input value={editForm.contact_person} onChange={e=>setEditForm({...editForm, contact_person:e.target.value})} data-testid="edit-lab-contact-person" /></div>
                <div><Label>Contact Email *</Label><Input type="email" required value={editForm.contact_email} onChange={e=>setEditForm({...editForm, contact_email:e.target.value})} data-testid="edit-lab-contact-email" /></div>
                <DialogFooter>
                  <Button type="submit" disabled={savingEdit} className="gap-2" data-testid="save-edit-lab-btn">
                    {savingEdit && <Loader2 className="w-4 h-4 animate-spin" />} Save Changes
                  </Button>
                </DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BookingsTab({ token, disabled }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`${API}/university/lab-bookings`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setRows((await res.json()).bookings || []);
    setLoading(false);
  }, [token]);
  useEffect(() => { refresh(); }, [refresh]);

  const act = async (id, action) => {
    const res = await fetch(`${API}/university/lab-bookings/${id}/${action}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { toast.success(action === 'approve' ? 'Approved' : 'Rejected'); refresh(); }
    else { const d = await res.json().catch(() => ({})); toast.error(d.detail || 'Failed'); }
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto my-6" />;
  if (!rows.length) return <div className="space-y-4">{disabled && <ModuleDisabledNotice module="Labs" testId="module-disabled-bookings" />}<p className="text-sm text-muted-foreground py-6 text-center">No lab booking requests yet.</p></div>;
  return (
    <div className="space-y-3">
      {disabled && <ModuleDisabledNotice module="Labs" testId="module-disabled-bookings" />}
      {rows.map(b => (
        <Card key={b.id} data-testid={`booking-${b.id}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
              <div>
                <h4 className="font-medium">{b.lab_name}</h4>
                <p className="text-xs text-muted-foreground">{b.industry_company || b.industry_email} · {new Date(b.created_at).toLocaleString()}</p>
              </div>
              <StatusBadge status={b.status} />
            </div>
            <p className="text-sm text-muted-foreground mb-2 whitespace-pre-wrap">{b.purpose}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mt-2 pt-2 border-t">
              <div><p className="text-[10px] text-muted-foreground">Dates</p><p className="font-medium">{b.requested_dates}</p></div>
              <div><p className="text-[10px] text-muted-foreground">Hours</p><p className="font-medium">{b.hours}</p></div>
              <div><p className="text-[10px] text-muted-foreground">Days</p><p className="font-medium">{b.days}</p></div>
              <div><p className="text-[10px] text-muted-foreground">Amount</p><p className="font-medium">{fmtINR(b.amount_paise)}</p></div>
            </div>
            {b.status === 'pending' && !disabled && (
              <div className="flex gap-2 mt-3">
                <Button size="sm" onClick={() => act(b.id, 'approve')} className="gap-1" data-testid={`approve-${b.id}`}><CheckCircle className="w-4 h-4" /> Approve</Button>
                <Button size="sm" variant="outline" onClick={() => act(b.id, 'reject')} className="gap-1" data-testid={`reject-${b.id}`}><XCircle className="w-4 h-4" /> Reject</Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: 'bg-amber-500/10 text-amber-700',
    approved: 'bg-blue-500/10 text-blue-700',
    rejected: 'bg-zinc-500/10 text-zinc-700',
    paid: 'bg-emerald-500/10 text-emerald-700',
  };
  return <Badge className={map[status] || 'bg-muted'}>{status}</Badge>;
}

/** Finance view scoped to the university (project revenues + lab bookings). */
function UniversityFinance({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      const res = await fetch(`${API}/university/finance`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setData(await res.json());
      setLoading(false);
    })();
  }, [token]);
  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto my-6" />;
  if (!data) return null;
  const t = data.totals || {};
  const downloadCsv = () => {
    const lines = ['Date,Type,Title,Status,Gross (INR),Earnings (INR)'];
    data.rows.forEach(r => lines.push([
      (r.date||'').slice(0,19), r.kind, JSON.stringify(r.title||''), r.status||'',
      (r.gross_paise/100).toFixed(2),
      ((r.kind==='project'?r.expert_share_paise:r.university_share_paise)/100).toFixed(2),
    ].join(',')));
    const url = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = 'collabhive-university-finance.csv'; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4"><p className="text-[10px] uppercase text-muted-foreground">Total Gross</p><p className="text-xl sm:text-2xl font-bold mt-1">{fmtINR(t.gross_paise)}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-[10px] uppercase text-muted-foreground">Innovators Earned (80%)</p><p className="text-xl sm:text-2xl font-bold mt-1">{fmtINR(t.expert_share_paise)}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-[10px] uppercase text-muted-foreground">University Earned (80% of labs)</p><p className="text-xl sm:text-2xl font-bold mt-1">{fmtINR(t.university_share_paise)}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-[10px] uppercase text-muted-foreground">Transactions</p><p className="text-xl sm:text-2xl font-bold mt-1">{data.rows?.length || 0}</p></CardContent></Card>
      </div>
      <div className="flex justify-end"><Button size="sm" variant="outline" onClick={downloadCsv} className="gap-2" data-testid="uni-download-csv"><Download className="w-4 h-4" /> Download CSV</Button></div>
      <Card>
        <CardContent className="p-4">
          <h4 className="text-sm font-semibold mb-2">Transactions</h4>
          {!data.rows?.length ? <p className="text-xs text-muted-foreground">No transactions.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground border-b"><tr><th className="text-left py-2 pr-2">Date</th><th className="text-left py-2 px-2">Item</th><th className="text-left py-2 px-2">Type</th><th className="text-right py-2 pl-2">Earnings</th></tr></thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-2 pr-2 whitespace-nowrap">{(r.date||'').slice(0,10)}</td>
                      <td className="py-2 px-2 max-w-[240px] truncate" title={r.title}>{r.title}</td>
                      <td className="py-2 px-2"><Badge variant="secondary" className="text-[10px]">{r.kind}</Badge></td>
                      <td className="py-2 pl-2 text-right font-medium">{fmtINR(r.kind==='project' ? r.expert_share_paise : r.university_share_paise)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/** Settings — Field Options + Revenue Splits. CollabHive share is locked. */
const REVENUE_KIND_META = [
  { key: 'labs', label: 'Lab Bookings' },
  { key: 'projects_referral', label: 'Projects — Referral Industry' },
  { key: 'projects_non_referral', label: 'Projects — Non-Referral Industry' },
  { key: 'meetings_referral', label: 'Virtual Meetings — Referral Industry' },
  { key: 'meetings_non_referral', label: 'Virtual Meetings — Non-Referral Industry' },
];

function SettingsTab({ token }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fieldOptions, setFieldOptions] = useState({ designation: [], department: [], school: [] });
  const [revenueSplits, setRevenueSplits] = useState({});
  const [lockedCollab, setLockedCollab] = useState({});

  useEffect(() => {
    (async () => {
      const res = await fetch(`${API}/university/settings`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        setFieldOptions(d.field_options);
        setRevenueSplits(d.revenue_splits);
        setLockedCollab(d.locked_collabhive_pct);
      }
      setLoading(false);
    })();
  }, [token]);

  const addOpt = (key) => setFieldOptions({ ...fieldOptions, [key]: [...(fieldOptions[key] || []), ''] });
  const updateOpt = (key, idx, val) => {
    const next = [...(fieldOptions[key] || [])];
    next[idx] = val;
    setFieldOptions({ ...fieldOptions, [key]: next });
  };
  const removeOpt = (key, idx) => {
    const next = [...(fieldOptions[key] || [])];
    next.splice(idx, 1);
    setFieldOptions({ ...fieldOptions, [key]: next });
  };

  const updateSplit = (kind, side, value) => {
    const n = Math.max(0, Math.min(100, Number(value) || 0));
    const locked = lockedCollab[kind] || 0;
    const remaining = 100 - locked;
    const other = side === 'university_pct' ? 'expert_pct' : 'university_pct';
    const nextOther = Math.max(0, remaining - n);
    setRevenueSplits({
      ...revenueSplits,
      [kind]: { ...revenueSplits[kind], [side]: n, [other]: nextOther, collabhive_pct: locked },
    });
  };

  const save = async () => {
    const payload = {
      field_options: {
        designation: (fieldOptions.designation || []).map(x => (x || '').trim()).filter(Boolean),
        department: (fieldOptions.department || []).map(x => (x || '').trim()).filter(Boolean),
        school: (fieldOptions.school || []).map(x => (x || '').trim()).filter(Boolean),
      },
      revenue_splits: revenueSplits,
    };
    setSaving(true);
    try {
      const res = await fetch(`${API}/university/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (res.ok) toast.success('Settings saved');
      else { const d = await res.json().catch(() => ({})); toast.error(d.detail || 'Failed'); }
    } finally { setSaving(false); }
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto my-6" />;

  return (
    <div className="space-y-6">
      {/* Onboarding Form Customisation */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><SettingsIcon className="w-4 h-4 text-primary" /> Onboarding Form Customisation</CardTitle>
          <p className="text-xs text-muted-foreground">Configure dropdown options for the expert referral signup form. The fields themselves (Designation, Department, School) are fixed; only options can be managed here.</p>
        </CardHeader>
        <CardContent className="space-y-5">
          {['designation', 'department', 'school'].map(key => (
            <div key={key}>
              <Label className="capitalize text-sm">{key === 'school' ? 'School / Faculty' : key}</Label>
              <div className="space-y-2 mt-2">
                {(fieldOptions[key] || []).map((opt, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      value={opt}
                      onChange={(e) => updateOpt(key, idx, e.target.value)}
                      placeholder={`Add an option…`}
                      data-testid={`opt-${key}-${idx}`}
                    />
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeOpt(key, idx)}><X className="w-4 h-4" /></Button>
                  </div>
                ))}
                <Button type="button" size="sm" variant="outline" onClick={() => addOpt(key)} className="gap-1" data-testid={`add-opt-${key}`}>
                  <Plus className="w-3 h-3" /> Add option
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Revenue Distribution Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><IndianRupee className="w-4 h-4 text-primary" /> Revenue Distribution</CardTitle>
          <p className="text-xs text-muted-foreground">Each row must sum to 100%. You can adjust the University and Expert shares; CollabHive's share is fixed per transaction type.</p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b">
                <tr><th className="text-left py-2 pr-2">Transaction Type</th><th className="text-right py-2 px-2 w-24">University %</th><th className="text-right py-2 px-2 w-24">Expert %</th><th className="text-right py-2 pl-2 w-28">CollabHive % (locked)</th></tr>
              </thead>
              <tbody>
                {REVENUE_KIND_META.map(({ key, label }) => {
                  const row = revenueSplits[key] || { university_pct: 0, expert_pct: 0, collabhive_pct: lockedCollab[key] || 0 };
                  return (
                    <tr key={key} className="border-b last:border-0">
                      <td className="py-2 pr-2">{label}</td>
                      <td className="py-2 px-2">
                        <Input type="number" min="0" max="100" value={row.university_pct} onChange={(e) => updateSplit(key, 'university_pct', e.target.value)} className="w-20 text-right" data-testid={`split-${key}-uni`} />
                      </td>
                      <td className="py-2 px-2">
                        <Input type="number" min="0" max="100" value={row.expert_pct} onChange={(e) => updateSplit(key, 'expert_pct', e.target.value)} className="w-20 text-right" data-testid={`split-${key}-expert`} />
                      </td>
                      <td className="py-2 pl-2 text-right">
                        <Badge variant="secondary">{lockedCollab[key]}%</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">Tip: changing University share auto-adjusts the Expert share so each row stays at 100%.</p>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="gap-2" data-testid="save-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
          Save Settings
        </Button>
      </div>
    </div>
  );
}

